import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { DonutChartComponent, DonutChartData } from '../../../../../../_shared/components/graphs/donut-chart/donut-chart.component';
import { BaseWidgetComponent } from '../../../../../../home/home-widgets/widgets/base-widget.component';
import { FilterService } from '../../../../../../_shared/services';
import { MaintenanceOverviewService } from '../../../services/maintenance-overview.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { ReactiveMaintenanceGraphModalComponent } from '../../modals/reactive-maintenance-graph-modal/reactive-maintenance-graph-modal.component';
import { AccountDateFilterRequest } from '../../../../../../_shared/models/accountDateFilter.model';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-reactive-maintenance-graph',
  standalone: true,
  imports: [CommonModule, DonutChartComponent, EmptyStateComponent, GraphTrendIconComponent, TranslocoDirective],
  templateUrl: './reactive-maintenance-graph.component.html',
  styleUrl: './reactive-maintenance-graph.component.scss'
})
export class ReactiveMaintenanceGraphComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  private maintenanceService = inject(MaintenanceOverviewService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(NgbModal);
  private transloco = inject(TranslocoService);

  protected data: DonutChartData[] = [];
  private readonly chartColors = ['#E46F2C', '#8D63C3', '#7F7FD7', '#DCC2E3', '#F4BC2B'];

  override title: string = this.transloco.translate('maintenanceOverview.graph.reactiveMaintenanceTitle');
  totalValue: number = 0;
  percentageChange: number = 0;
  hoveredPercentage: number | null = null;
  hoveredSegmentName: string | null = null;

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);

    this.isLoading = true;
  }

  ngOnInit(): void {
    this.loadReactiveMaintenanceData();
  }

  override ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    await this.loadReactiveMaintenanceData();
  }

  private async loadReactiveMaintenanceData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();
      const request: AccountDateFilterRequest = {
        accountIds: this.filterService.filter.accountIds ?? [],
        timeFrom: this.filterService.filter.startDate!,
        timeTo: this.filterService.filter.endDate!,
        interval: this.filterService.filter.interval
      };

      const [summaryResponse, reactiveMaintenanceResponse] = await Promise.all([
        this.maintenanceService.getReactiveMaintenanceSummary({
          ...request,
          previousTimeFrom: this.filterService.filter.previousCycleStartDate!,
          previousTimeTo: this.filterService.filter.previousCycleEndDate!
        }, this.cancelSubject),
        this.maintenanceService.getReactiveMaintenance(request, this.cancelSubject)
      ]);

      this.totalValue = summaryResponse.data?.totalValue ?? 0;
      this.percentageChange = summaryResponse.data?.percentageChange ?? 0;

      const data = reactiveMaintenanceResponse.data;
      if (data) {
        this.data = data
          .map(item => ({
            name: item.localizedMaintenanceReason || item.maintenanceReason,
            value: item.maintenanceCount,
            color: ''
          }))
          .sort((a, b) => b.value - a.value)
          .map((item, index) => ({
            ...item,
            color: this.chartColors[index % this.chartColors.length]
          }));
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  formatValue(): string {
    return `${this.totalValue.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    // For reactive maintenance, downtrend is positive (less reactive is better)
    return this.percentageChange < 0;
  }

  hasData(): boolean {
    if (!this.data || this.data.length === 0) return false;

    return this.data.some(item => {
      const value = Number(item.value) || 0;
      return value > 0;
    });
  }

  /** `label` is the reason text the donut was built with (already in the user's language), so it matches the hover key. */
  get legendItems() {
    return this.data.map(item => ({
      label: item.name,
      color: item.color,
      percentage: ((item.value / this.data.reduce((sum, d) => sum + d.value, 0)) * 100).toFixed(0)
    }));
  }

  onSegmentHover(event: { percentage: number; name: string } | null): void {
    if (event) {
      this.hoveredPercentage = event.percentage;
      this.hoveredSegmentName = event.name;
    } else {
      this.hoveredPercentage = null;
      this.hoveredSegmentName = null;
    }
  }

  getCenterPercentage(): string {
    return this.hoveredPercentage !== null
      ? `${this.hoveredPercentage}%`
      : this.transloco.translate('maintenanceOverview.graph.percentage');
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    this.modalService.open(ReactiveMaintenanceGraphModalComponent, modalOptions);
  }
}